// ABOUTME: Displays family behavior log with child and behavior type filtering
// ABOUTME: Uses BehaviorEventsTable for tabular display with edit/delete actions

import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress
} from '@mui/material';
import BehaviorEventsTable, { BehaviorEventEntry } from '../BehaviorEventsTable';
import { BehaviorType } from '../../types/api';
import {
  getBehaviorIcon,
  BEHAVIOR_CONFIG,
  BehaviorFilterState
} from '../../utils/behaviorUtils';
import { getChildDropdownOptions } from '../../utils/filterUtils';
import type { FamilyDetailData } from './types';

interface FamilyBehaviorLogSectionProps {
  family: FamilyDetailData;
  behaviors: BehaviorEventEntry[];
  behaviorTotalCount: number;
  behaviorPage: number;
  behaviorPageSize: number;
  behaviorLoading: boolean;
  behaviorFilters: BehaviorFilterState;
  canEditDeleteBehavior: (behavior: BehaviorEventEntry) => boolean;
  onBehaviorFiltersChange: (filters: BehaviorFilterState) => void;
  onBehaviorPageChange: (page: number) => void;
  onBehaviorRowsPerPageChange: (rowsPerPage: number) => void;
  onBehaviorSort: (field: string) => void;
  onEditBehavior: (behavior: BehaviorEventEntry) => void;
  onDeleteBehavior: (behavior: BehaviorEventEntry) => void;
  onPageChange: (newPage: number) => void;
}

const FamilyBehaviorLogSection: React.FC<FamilyBehaviorLogSectionProps> = ({
  family,
  behaviors,
  behaviorTotalCount,
  behaviorPage,
  behaviorPageSize,
  behaviorLoading,
  behaviorFilters,
  canEditDeleteBehavior,
  onBehaviorFiltersChange,
  onBehaviorPageChange,
  onBehaviorRowsPerPageChange,
  onBehaviorSort,
  onEditBehavior,
  onDeleteBehavior,
  onPageChange
}) => {
  const scrollPositionRef = useRef<number>(0);
  const pendingScrollRestoreRef = useRef<boolean>(false);

  // Restore scroll position after loading completes
  useEffect(() => {
    if (!behaviorLoading && pendingScrollRestoreRef.current) {
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPositionRef.current);
        pendingScrollRestoreRef.current = false;
      });
    }
  }, [behaviorLoading]);

  const handleFilterChange = (callback: () => void) => {
    // Save current scroll position
    scrollPositionRef.current = window.scrollY;
    pendingScrollRestoreRef.current = true;

    // Apply filter change (this will trigger loading)
    callback();
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Family Behavior Log
        </Typography>

        {/* Filter Controls */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          flexWrap: 'wrap',
          mb: 2
        }}>
          {/* Child Filter Dropdown */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by child</InputLabel>
            <Select
              value={behaviorFilters.selectedChildId?.toString() || ''}
              label="Filter by child"
              onChange={(e) => {
                handleFilterChange(() => {
                  const value = e.target.value === '' ? null : Number(e.target.value);
                  onBehaviorFiltersChange({ ...behaviorFilters, selectedChildId: value });
                  if (behaviorPage !== 1) {
                    onPageChange(1);
                  }
                });
              }}
              aria-label="Filter by child"
              MenuProps={{
                disableScrollLock: true,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                }
              }}
            >
              {family && getChildDropdownOptions(family.children).map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value?.toString() || ''}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Behavior Type Filter Checkboxes */}
          <FormGroup row sx={{ gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(BEHAVIOR_CONFIG).map(([typeKey, config]) => {
              const behaviorType = parseInt(typeKey) as BehaviorType;
              return (
                <FormControlLabel
                  key={behaviorType}
                  control={
                    <Checkbox
                      checked={behaviorFilters.selectedBehaviorTypes.includes(behaviorType)}
                      onChange={(e) => {
                        handleFilterChange(() => {
                          const newTypes = e.target.checked
                            ? [...behaviorFilters.selectedBehaviorTypes, behaviorType]
                            : behaviorFilters.selectedBehaviorTypes.filter(type => type !== behaviorType);
                          onBehaviorFiltersChange({ ...behaviorFilters, selectedBehaviorTypes: newTypes });
                          if (behaviorPage !== 1) {
                            onPageChange(1);
                          }
                        });
                      }}
                      aria-label={`Filter by ${config.label.toLowerCase()} behavior`}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {/* Icon glyph sizing via fontSize, not a Typography text override */}
                      {getBehaviorIcon(behaviorType, { sx: { fontSize: 16 } })}
                      <Typography variant="body2">{config.label}</Typography>
                    </Box>
                  }
                />
              );
            })}
          </FormGroup>
        </Box>

        {behaviorLoading && behaviors.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <BehaviorEventsTable
            events={behaviors}
            totalCount={behaviorTotalCount}
            page={behaviorPage - 1}
            rowsPerPage={behaviorPageSize}
            loading={behaviorLoading}
            showChildColumn={true}
            showFamilyColumn={false}
            showEditActions={true}
            onPageChange={onBehaviorPageChange}
            onRowsPerPageChange={onBehaviorRowsPerPageChange}
            onSort={onBehaviorSort}
            onEdit={onEditBehavior}
            onDelete={onDeleteBehavior}
            canEditDelete={canEditDeleteBehavior}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default FamilyBehaviorLogSection;
